import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useTranslation } from "react-i18next";

import { AddHeader } from "../../components/AddHeader";
import { ExerciseCard } from "../../components/ExerciseCard";
import {
  updateexercise,
  getexercise,
} from "../../components/funtionsBD/Mainservice";
import { ExerciseAddLayout } from "./ExerciseAddLayout";
import { currentAppLanguage } from "../../utils/appLanguages";
import { localizedExerciseName, localizedField } from "../../utils/localizedCatalogFields";

export const Changeex = () => {
  const [error, seterror] = useState();
  const [open, setopen] = useState(false);
  const [success, setsuccess] = useState();
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replacingExId, setReplacingExId] = useState(null);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state || {};
  const { t, i18n } = useTranslation();
  const lng = currentAppLanguage(i18n);

  useEffect(() => {
    if (!data?.teid) return;
    const load = async () => {
      try {
        setLoading(true);
        setCatalog([]);
        const params = {
          type_id: data.id || undefined,
          sub_type_id: data.subid || undefined,
          cb_id: data.cbid || undefined,
          group_id: data.grpid || undefined,
        };
        const response = await getexercise(params);
        setCatalog(
          (response?.data?.data || []).map((ex) => ({
            ...ex,
            duration: ex.duration ?? 10,
          }))
        );
      } catch (err) {
        console.error("Catalog load error:", err);
        seterror(
          err?.message ||
            t("errorLoadingExercises") ||
            "Could not load catalog exercises"
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [data?.grpid, data?.teid, data?.id, data?.subid, data?.cbid, t]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((ex) => {
      const name = localizedExerciseName(ex, lng).toLowerCase();
      const objective = localizedField(ex, "ex_objective", lng).toLowerCase();
      return name.includes(q) || objective.includes(q);
    });
  }, [catalog, query, lng]);

  const handleReplace = async (recEx) => {
    if (!recEx?.ex_id || !data?.teid || !data?.exid) return;
    if (String(recEx.ex_id) === String(data.exid)) {
      navigate("/planning", { state: { session: location.state?.session } });
      return;
    }
    try {
      setReplacingExId(recEx.ex_id);
      // Slot already has a catalog exercise (this page requires data.exid) — do not
      // send cbid; catalog competency_block ids are not stored as ex_sub_type_id.
      await updateexercise({
        te_id: data.teid,
        replace_ex_id: recEx.ex_id,
        existing_ex_id: data.exid,
      });
      setsuccess(t("exerciseReplaced") || "Exercise replaced successfully");
      setopen(true);
      setTimeout(
        () => navigate("/planning", { state: { session: location.state?.session } }),
        1000
      );
    } catch (err) {
      seterror(err?.message || t("errorReplacingExercise") || "Error replacing exercise");
    } finally {
      setReplacingExId(null);
    }
  };

  const filterLabel = data.cb || data.subtype || data.type || "";

  return (
    <ExerciseAddLayout>
      <AddHeader title={t("exchangeExercise") || t("changeex") || "Exchange exercise"} />
      <p className="exercise_type_define" style={{ marginBottom: "8px" }}>
        {filterLabel
          ? `${filterLabel} → ${t("exchangeExerciseHint") || "Same skill or competency block — tap a drill to switch."}`
          : t("exchangeExerciseHint") ||
            "Same skill or competency block — tap a drill to switch."}
      </p>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={
          t("searchExercisesByNameOrDescription") ||
          "Search by name or description…"
        }
        style={{
          width: "100%",
          maxWidth: 480,
          marginBottom: 12,
          padding: "10px 12px",
          borderRadius: 12,
          border: "1px solid #e2e8f0",
          font: "inherit",
        }}
      />
      <p className="error_msg">{error}</p>

      {loading ? (
        <div className="exercise-add-muted-text" style={{ padding: "24px", textAlign: "center" }}>
          {t("loading") || "Loading..."}
        </div>
      ) : filtered.length === 0 ? (
        <div className="exercise-add-muted-text" style={{ padding: "24px", textAlign: "center" }}>
          {t("noCatalogExercisesFound") || "No catalog exercises found for this skill or block."}
        </div>
      ) : (
        <div className="expage-grid">
          {filtered.map((rec) => (
            <ExerciseCard
              key={rec.ex_id}
              exercise={rec}
              isSelected={
                replacingExId === rec.ex_id || String(rec.ex_id) === String(data.exid)
              }
              onSelect={() => !replacingExId && handleReplace(rec)}
              lng={lng}
              t={t}
              selectMode="single"
              showDurationInput={false}
            />
          ))}
        </div>
      )}
      {replacingExId && (
        <p
          className="exercise-add-muted-text"
          style={{ marginTop: "8px", fontSize: "12px", textAlign: "center" }}
        >
          {t("exchangingExercise") || "Switching exercise…"}
        </p>
      )}
      <Snackbar open={open} autoHideDuration={4000} onClose={() => setopen(false)}>
        <Alert variant="filled" severity="success">
          {success}
        </Alert>
      </Snackbar>
    </ExerciseAddLayout>
  );
};
